import { createAffiliationRequest } from '@api/affiliationRequest';
import useToastContext from '@contexts/ToastContext/hook';
import { AffiliationConfigModes } from '@interfaces/affiliationConfig';
import {
  affiliationRequestToCreateSchema,
  AffiliationRequestToCreate,
} from '@interfaces/affiliationRequest';
import { Button } from '@vibTheme/components/_base/Button';
import {
  InputExternalValidation,
  InputText,
  InputTextArea,
} from '@vibTheme/components/_base/Input';
import { Typography } from '@vibTheme/components/_base/Typography';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { AffiliationMarketProductProps } from './AffiliationMarketProductCard';
import { IMAGE_NOT_FOUND } from '@utils/imageNotFound';
import { Image } from '@vibTheme/components/_base/Image';
import { useExternalValidationResolver } from '@hooks/useExternalValidationResolver';
import { isAffiliationPixKeyValid } from '@interfaces/affiliation';
import { AffiliationMarketProductInfo } from './AffiliationMarketProductInfo';
import useGroupsContext from '@contexts/GroupsContext/hook';
import useFeaturePermissionsContext from '@contexts/FeaturePermissionsContext/hook';
import { PixKeyFormatHelper } from '@components/PixKeyFormatHelper';
import { Help } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { VibTooltip } from '@vibTheme/components/VibTooltip';

export const CreateAffiliationRequestForm = (
  props: AffiliationMarketProductProps,
) => {
  const { product } = props;
  const [isCreating, setIsCreating] = useState(false);

  const { fetchGroups } = useGroupsContext();
  const { refreshPermissions } = useFeaturePermissionsContext();
  const toast = useToastContext();
  const router = useRouter();

  const {
    externalValidationResolver,
    isLoading: isExternalValidationLoading,
    isValid: isExternalValidationValid,
  } = useExternalValidationResolver(affiliationRequestToCreateSchema, [
    { name: 'pix', validate: isAffiliationPixKeyValid(product.id) },
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<AffiliationRequestToCreate>({
    mode: 'onChange',
    resolver: externalValidationResolver,
  });

  const onSubmit: SubmitHandler<AffiliationRequestToCreate> = async (data) => {
    setIsCreating(true);

    try {
      await createAffiliationRequest(product.botConfigId, data);
      /*
        A permissão vai JUNTO com os grupos.

        O recorte das telas é por permissão desde a #340, e `getUserPermissions` fecha o que não
        está no mapa — produto ausente e mapa ausente dão a mesma resposta, de propósito. Atualizar
        só o `GroupsContext` deixa a comunidade recém-criada alcançável e sem permissão nenhuma: ela
        some das filas e dos relatórios até um recarregamento manual.

        É o par que o aceite de convite já fazia (`pages/convite`).
      */
      /*
        Os dois devolvem `boolean` e NÃO lançam: a falha do mapa efetivo zera o que havia e resolve
        `false`. Ignorar o retorno anuncia sucesso sobre estado velho — e, no caso da permissão,
        sobre estado ZERADO, onde a plataforma inteira fica sem controle nenhum até um recarregamento.

        Mesma leitura que `pages/convite` faz: o desfecho separa "deu certo" de "deu certo e a sessão
        já sabe disso".
      */
      const [gruposOk, permissoesOk] = await Promise.all([
        fetchGroups(),
        refreshPermissions(),
      ]);
      const sessaoAtualizada = gruposOk && permissoesOk;

      toast.setContent(
        sessaoAtualizada
          ? 'Solicitação de afiliação criada com sucesso!'
          : 'Solicitação de afiliação criada, mas esta sessão não conseguiu se atualizar. Recarregue a página.',
        sessaoAtualizada ? 'success' : 'warning',
      );

      if (product.mode === AffiliationConfigModes['Liberado para todos'])
        router.push(`/products/${product.productCode}/outreachLinks`);
      else if (product.mode === AffiliationConfigModes['Mediante aprovação'])
        router.push(`/affiliation/affiliationRequests`);
    } catch (err: any) {
      const errMessage =
        err.detail || 'Ocorreu um erro ao criar a solicitação de afiliação.';
      if (errMessage === 'O usuário já é afiliado da comunidade requisitada.') {
        delete err.detail;
        toast.handleError(
          err,
          'Você já é afiliado deste produto! Você pode acessar o link de vendas pelo menu Comunidades ou Afiliações > Comunidades Afiliadas.',
        );
      } else {
        toast.handleError(err, errMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex gap-y-md w-full">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-md w-full "
      >
        <Typography component="h4">
          Formulário para pedido de afiliação
        </Typography>
        <div className="flex flex-col gap-md md:flex-row">
          <div className="relative w-full md:w-1/3 md:min-w-[12rem] md:max-w-[16rem] h-[18rem] rounded-default">
            <Image
              src={product.image || IMAGE_NOT_FOUND}
              alt={`Imagem de ${product.name}`}
              className={`${
                product.image ? 'object-cover' : 'object-contain'
              } rounded-t-default`}
              fill
            />
          </div>
          <div className="flex flex-col gap-md w-full">
            <InputText
              name="contact"
              control={control}
              label="Contato"
              error={!!errors.contact}
              helperText={errors.contact?.message}
            />

            <InputExternalValidation
              name="pix"
              control={control}
              label="Chave Pix"
              error={!!errors.pix}
              helperText={errors.pix?.message}
              isLoading={isExternalValidationLoading.pix}
              isValid={isExternalValidationValid.pix}
              InputProps={{
                endAdornment: (
                  <VibTooltip title={<PixKeyFormatHelper />}>
                    <IconButton>
                      <Help />
                    </IconButton>
                  </VibTooltip>
                ),
              }}
            />
            <InputTextArea
              name="message"
              control={control}
              label="Mensagem"
              error={!!errors.message}
              helperText={errors.message?.message}
            />
          </div>
        </div>
        <AffiliationMarketProductInfo
          descriptionLabel="Descrição da comunidade"
          product={product}
        />
        <Button
          type="submit"
          className="w-full md:w-32 h-[2.7rem] ml-auto"
          loading={isCreating}
          disabled={!isValid}
        >
          Enviar
        </Button>
      </form>
    </div>
  );
};
